/**
 * EmailSendingSection — White-Label → Email → "Email sending".
 *
 * Which mail server an organization's platform email (invites, password
 * resets) goes out through: the platform default, or the organization's own
 * SMTP server. A custom server is used for real mail only once a test with
 * the CURRENT settings has succeeded; any change returns it to "test
 * required". An organization with no sender of its own uses the first active
 * one above it if that organization shares it (shown
 * read-only, linked), else the platform default — naming the organization
 * above that keeps its sender to itself (email_inheritance.py).
 *
 * The test mail goes to the sender's own From address — the mailbox gets a
 * mail from itself — or an address typed in "Send the test to"; never to the
 * person clicking. The From may be an alias of the sign-in account; a server
 * that will not send as it is answered with what to set up at the provider.
 *
 * Super admin only — the backend enforces it on every route. The password is
 * write-only: the API answers `has_password`, never the value, and asks for it
 * again when the server or username changes. Errors arrive as codes worded
 * here (`errors.*` for a refused setting, `smtp.*` for a failed send); the
 * server's own reply is shown as it came, isolated.
 */
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Input, InputNumber, Radio, Select, Spin, message as antdMessage } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing/paths';
import { formatExactTime } from '@/utils/relativeTime';
import { bidiIsolate } from '@/utils/bidi';

type Mode = 'platform' | 'custom';
type Security = 'starttls' | 'ssl';

interface OwnSender {
  mode: Mode;
  from_address: string;
  reply_to: string;
  smtp_host: string;
  smtp_port: number | null;
  smtp_security: Security;
  smtp_username: string;
  has_password: boolean;
  status: 'platform' | 'test_required' | 'active';
  active_since: string | null;
  last_test_at: string | null;
  last_test_error: SmtpError | null;
  last_failure: (SmtpError & { at: string }) | null;
}

/** A failed send: what went wrong (a code worded here) and the server's own words. */
interface SmtpError {
  code: string;
  detail: string;
}

interface SenderSource {
  kind: 'platform' | 'custom';
  from_address: string;
  source_tenant_id: string | null;
  source_tenant_name: string | null;
  /** An organization above with an active sender it does not share. */
  not_shared_by: string | null;
}

interface SenderView {
  own: OwnSender;
  from_above: SenderSource;
}

interface TestOutcome {
  error: SmtpError | null;
  to: string;
}

interface Draft {
  mode: Mode;
  from_address: string;
  reply_to: string;
  smtp_host: string;
  smtp_port: number | null;
  smtp_security: Security;
  smtp_username: string;
  smtp_password: string;
}

const DEFAULT_PORT: Record<Security, number> = { starttls: 587, ssl: 465 };

/** Which provider's words an alias refusal gets, by the server it signs in to. */
function aliasProvider(host: string): 'google' | 'microsoft' | null {
  const h = host.toLowerCase();
  if (/(^|\.)(gmail|googlemail|google)\.com$/.test(h)) return 'google';
  if (/(^|\.)(office365|outlook)\.com$/.test(h)) return 'microsoft';
  return null;
}

function draftOf(own: OwnSender): Draft {
  return {
    mode: own.mode,
    from_address: own.from_address,
    reply_to: own.reply_to,
    smtp_host: own.smtp_host,
    smtp_port: own.smtp_port ?? DEFAULT_PORT[own.smtp_security],
    smtp_security: own.smtp_security,
    smtp_username: own.smtp_username,
    smtp_password: '',
  };
}

export default function EmailSendingSection({
  tenantId, onSenderChange,
}: {
  tenantId: string;
  /** Called after every save / test, so the email preview re-reads its From. */
  onSenderChange: () => void;
}) {
  const { t } = useLanguage();
  const ids = useId();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const [view, setView] = useState<SenderView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; code: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestOutcome | null>(null);
  // The provider help below the password: the password field's advice opens it.
  const [helpOpen, setHelpOpen] = useState(false);
  // "Send the test to": follows the From address until someone types here.
  const [testTo, setTestTo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setLoadError(null);
    setTestResult(null);
    (async () => {
      try {
        const res = await api.get(`/super-admin/tenants/${tenantId}/email-sender`);
        if (!cancelled) {
          setView(res.data);
          setDraft(draftOf(res.data.own));
        }
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.response?.data?.error || '');
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  const dirty = useMemo(() => {
    if (!view || !draft) return false;
    const saved = draftOf(view.own);
    return (Object.keys(saved) as (keyof Draft)[]).some(k => saved[k] !== draft[k]);
  }, [view, draft]);

  if (loadError !== null) {
    return <div className="wl-field-hint" role="alert">{loadError || t('whiteLabel.emailSending.loadFailed')}</div>;
  }
  if (!view || !draft) {
    return <div className="wl-email-sending-loading"><Spin /></div>;
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft(d => (d ? { ...d, [key]: value } : d));
    if (fieldError?.field === key) setFieldError(null);
    setTestResult(null); // it described the settings as they were
  };
  const errorFor = (field: string) => (
    fieldError?.field === field ? t(`whiteLabel.emailSending.errors.${fieldError.code}`) : undefined
  );
  const id = (field: string) => `${ids}-${field}`;

  const apply = (data: SenderView) => {
    setView(data);
    setDraft(draftOf(data.own));
    onSenderChange();
  };

  // Saves the draft; answers whether it was stored.
  const save = async (): Promise<boolean> => {
    setSaving(true);
    setFieldError(null);
    try {
      const body: Record<string, unknown> = { ...draft };
      if (!draft.smtp_password) delete body.smtp_password;
      const res = await api.put(`/super-admin/tenants/${tenantId}/email-sender`, body);
      apply(res.data);
      setTestResult(null);
      return true;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.field && data?.error_code) setFieldError({ field: data.field, code: data.error_code });
      antdMessage.error(data?.error_code
        ? t(`whiteLabel.emailSending.errors.${data.error_code}`)
        : t('whiteLabel.emailSending.saveFailed'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (dirty && !(await save())) return;
    setTesting(true);
    try {
      const res = await api.post(`/super-admin/tenants/${tenantId}/email-sender/test`,
        { to: (testTo ?? draft.from_address).trim() });
      apply(res.data);
      setTestResult({ error: res.data.error, to: res.data.to });
    } catch (err: any) {
      const data = err?.response?.data;
      const code = data?.error_code;
      if (data?.field && code) setFieldError({ field: data.field, code });
      antdMessage.error(code ? t(`whiteLabel.emailSending.errors.${code}`) : t('whiteLabel.emailSending.testFailed'));
    } finally {
      setTesting(false);
    }
  };

  const { own, from_above: above } = view;
  // A failed send in the viewer's language, about the SAVED settings (what
  // was tested / sent), followed by the server's own words as they came.
  const smtpError = (error: SmtpError) => {
    const provider = error.code === 'alias_refused' ? aliasProvider(own.smtp_host) : null;
    return (
      <>
        <div>{t(`whiteLabel.emailSending.smtp.${error.code}${provider ? `_${provider}` : ''}`, {
          server: bidiIsolate(`${own.smtp_host}:${own.smtp_port ?? ''}`),
          from: bidiIsolate(own.from_address),
          alias: bidiIsolate(own.from_address),
          account: bidiIsolate(own.smtp_username),
        })}</div>
        {error.detail && <div><bdi>{error.detail}</bdi></div>}
      </>
    );
  };
  const openOrg = (id: string) => navigate(tenantPath(`/super-admin/tenants/${id}/branding`));
  const fallbackLine = above.kind === 'custom'
    ? t('whiteLabel.emailSending.untilThenInherited', { org: bidiIsolate(above.source_tenant_name ?? ''), address: bidiIsolate(above.from_address) })
    : t('whiteLabel.emailSending.untilThenPlatform', { address: bidiIsolate(above.from_address) });

  return (
    <section className="wl-section" data-testid="wl-email-sending">
      <header className="wl-section-header">
        <h4 className="wl-section-title">{t('whiteLabel.emailSending.title')}</h4>
        <p className="wl-section-subtitle">{t('whiteLabel.emailSending.subtitle')}</p>
      </header>
      <div className="wl-section-body">
        <div className="wl-field">
          <label className="wl-field-label" id={id('mode')}>{t('whiteLabel.emailSending.sendThrough')}</label>
          <Radio.Group
            aria-labelledby={id('mode')}
            value={draft.mode}
            onChange={e => set('mode', e.target.value)}
            data-testid="wl-email-sending-mode"
          >
            <Radio value="platform">
              {above.kind === 'custom'
                ? t('whiteLabel.emailSending.modeInherited', { org: bidiIsolate(above.source_tenant_name ?? '') })
                : t('whiteLabel.emailSending.modePlatform')}
            </Radio>
            <Radio value="custom">{t('whiteLabel.emailSending.modeCustom')}</Radio>
          </Radio.Group>
        </div>

        {draft.mode === 'platform' && (
          above.kind === 'custom' ? (
            <div className="wl-email-sending-inherited" data-testid="wl-email-sending-inherited">
              <div className="wl-email-sending-inherited-head">
                {/* One sentence with the link inside it, as the radio label
                    reads: Hebrew joins the name to "מ־", English keeps its
                    space — never a space of our own between them. The tag is
                    <org>, not <link>: Trans's parser takes <link> for the
                    HTML void element and puts its content after it. */}
                <Trans
                  i18nKey="whiteLabel.emailSending.inheritedFromOrg"
                  values={{ org: above.source_tenant_name ?? '' }}
                  components={{ org: <OrgLink onClick={() => openOrg(above.source_tenant_id!)} /> }}
                />
              </div>
              <div className="wl-field-hint">
                {t('whiteLabel.emailSending.inheritedBody', { address: bidiIsolate(above.from_address) })}
              </div>
            </div>
          ) : (
            <div className="wl-field-hint" data-testid="wl-email-sending-platform">
              {t('whiteLabel.emailSending.platformBody', { address: bidiIsolate(above.from_address) })}
              {above.not_shared_by && (
                <div data-testid="wl-email-sending-not-shared">
                  {t('whiteLabel.emailSending.notSharedBy', { org: bidiIsolate(above.not_shared_by) })}
                </div>
              )}
            </div>
          )
        )}

        {draft.mode === 'custom' && (<>
          <SenderStatus own={own} dirty={dirty} fallbackLine={fallbackLine} smtpError={smtpError} />

          <div className="wl-field">
            <label className="wl-field-label" htmlFor={id('from_address')}>{t('whiteLabel.emailSending.fromAddress')}</label>
            <Input
              dir="ltr"
              value={draft.from_address}
              placeholder="hello@example.com"
              status={errorFor('from_address') ? 'error' : undefined}
              onChange={e => set('from_address', e.target.value)}
              id={id('from_address')}
              data-testid="wl-email-sending-from"
            />
            <div className="wl-field-hint">{errorFor('from_address') || t('whiteLabel.emailSending.fromAddressHint')}</div>
          </div>
          <div className="wl-field">
            <label className="wl-field-label" htmlFor={id('reply_to')}>{t('whiteLabel.emailSending.replyTo')}</label>
            <Input
              dir="ltr"
              value={draft.reply_to}
              placeholder={t('whiteLabel.emailSending.optional')}
              status={errorFor('reply_to') ? 'error' : undefined}
              onChange={e => set('reply_to', e.target.value)}
              id={id('reply_to')}
              data-testid="wl-email-sending-reply-to"
            />
            <div className="wl-field-hint">{errorFor('reply_to') || t('whiteLabel.emailSending.replyToHint')}</div>
          </div>
          <div className="wl-email-sending-server">
            <div className="wl-field">
              <label className="wl-field-label" htmlFor={id('smtp_host')}>{t('whiteLabel.emailSending.host')}</label>
              <Input
                dir="ltr"
                value={draft.smtp_host}
                placeholder="smtp.example.com"
                status={errorFor('smtp_host') ? 'error' : undefined}
                onChange={e => set('smtp_host', e.target.value)}
                id={id('smtp_host')}
                data-testid="wl-email-sending-host"
              />
              {errorFor('smtp_host') && <div className="wl-field-hint">{errorFor('smtp_host')}</div>}
            </div>
            <div className="wl-field">
              <label className="wl-field-label" htmlFor={id('smtp_port')}>{t('whiteLabel.emailSending.port')}</label>
              <InputNumber
                min={1}
                max={65535}
                value={draft.smtp_port}
                status={errorFor('smtp_port') ? 'error' : undefined}
                onChange={v => set('smtp_port', v ?? null)}
                id={id('smtp_port')}
                data-testid="wl-email-sending-port"
              />
            </div>
            <div className="wl-field">
              <label className="wl-field-label" htmlFor={id('smtp_security')}>{t('whiteLabel.emailSending.security')}</label>
              <Select
                value={draft.smtp_security}
                onChange={(v: Security) => {
                  // Follow the port when it is still the other mode's default.
                  if (draft.smtp_port === DEFAULT_PORT[draft.smtp_security]) set('smtp_port', DEFAULT_PORT[v]);
                  set('smtp_security', v);
                }}
                options={[
                  { value: 'starttls', label: t('whiteLabel.emailSending.securityStarttls') },
                  { value: 'ssl', label: t('whiteLabel.emailSending.securitySsl') },
                ]}
                id={id('smtp_security')}
                data-testid="wl-email-sending-security"
              />
            </div>
          </div>
          <div className="wl-field-hint wl-email-sending-server-hint">{t('whiteLabel.emailSending.securityHint')}</div>
          <div className="wl-field">
            <label className="wl-field-label" htmlFor={id('smtp_username')}>{t('whiteLabel.emailSending.username')}</label>
            <Input
              dir="ltr"
              value={draft.smtp_username}
              autoComplete="off"
              status={errorFor('smtp_username') ? 'error' : undefined}
              onChange={e => set('smtp_username', e.target.value)}
              id={id('smtp_username')}
              data-testid="wl-email-sending-username"
            />
            <div className="wl-field-hint">{errorFor('smtp_username') || t('whiteLabel.emailSending.usernameHint')}</div>
          </div>
          <div className="wl-field">
            <label className="wl-field-label" htmlFor={id('smtp_password')}>{t('whiteLabel.emailSending.password')}</label>
            <Input.Password
              dir="ltr"
              value={draft.smtp_password}
              autoComplete="new-password"
              placeholder={own.has_password ? t('whiteLabel.emailSending.passwordSaved') : ''}
              status={errorFor('smtp_password') ? 'error' : undefined}
              onChange={e => set('smtp_password', e.target.value)}
              id={id('smtp_password')}
              data-testid="wl-email-sending-password"
            />
            <div className="wl-field-hint">
              {errorFor('smtp_password')
                || (own.has_password ? t('whiteLabel.emailSending.passwordSavedHint') : t('whiteLabel.emailSending.passwordHint'))}
            </div>
            <div className="wl-field-hint" data-testid="wl-email-sending-password-advice">
              {t('whiteLabel.emailSending.passwordAdvice')}{' '}
              <button
                type="button"
                className="wl-email-sending-help-link"
                onClick={() => setHelpOpen(true)}
                aria-controls={id('help')}
                data-testid="wl-email-sending-password-help"
              >
                {t('whiteLabel.emailSending.passwordAdviceHelp')}
              </button>
            </div>
          </div>

          <details
            className="wl-email-sending-help"
            id={id('help')}
            open={helpOpen}
            onToggle={e => setHelpOpen((e.currentTarget as HTMLDetailsElement).open)}
          >
            <summary>{t('whiteLabel.emailSending.helpTitle')}</summary>
            <ul>
              <li>{t('whiteLabel.emailSending.helpGoogle')}</li>
              <li>{t('whiteLabel.emailSending.helpMicrosoft')}</li>
              <li>{t('whiteLabel.emailSending.helpServices')}</li>
              <li>{t('whiteLabel.emailSending.helpSpf')}</li>
            </ul>
          </details>
        </>)}

        {draft.mode === 'custom' && (
          <div className="wl-field">
            <label className="wl-field-label" htmlFor={id('test_to')}>{t('whiteLabel.emailSending.testTo')}</label>
            <Input
              dir="ltr"
              value={testTo ?? draft.from_address}
              status={errorFor('test_to') ? 'error' : undefined}
              onChange={e => { setTestTo(e.target.value); if (fieldError?.field === 'test_to') setFieldError(null); }}
              id={id('test_to')}
              data-testid="wl-email-sending-test-to"
            />
            <div className="wl-field-hint">{errorFor('test_to') || t('whiteLabel.emailSending.testToHint')}</div>
          </div>
        )}
        <div className="wl-email-sending-actions">
          {draft.mode === 'custom' && (
            <Button
              type="primary"
              onClick={test}
              loading={testing}
              disabled={saving}
              data-testid="wl-email-sending-test"
            >
              {t('whiteLabel.emailSending.test')}
            </Button>
          )}
          {(dirty || draft.mode === 'custom') && (
            <Button
              onClick={save}
              loading={saving}
              disabled={!dirty || testing}
              data-testid="wl-email-sending-save"
            >
              {t('whiteLabel.emailSending.save')}
            </Button>
          )}
        </div>

        {testResult && (
          <div
            role="status"
            className={`wl-email-sending-result wl-email-sending-result--${testResult.error === null ? 'ok' : 'fail'}`}
            data-testid="wl-email-sending-test-result"
          >
            {testResult.error === null
              ? t('whiteLabel.emailSending.testOk', { to: bidiIsolate(testResult.to) })
              : <>{t('whiteLabel.emailSending.testNotOk')}{smtpError(testResult.error)}</>}
          </div>
        )}
      </div>
    </section>
  );
}

/** The organization's name as a link, bidi-isolated inside the sentence
 * (Trans fills its children with the name). */
function OrgLink({ children, onClick }: { children?: ReactNode; onClick?: () => void }) {
  return (
    <Button
      type="link"
      size="small"
      className="wl-email-sending-org-link"
      onClick={onClick}
      data-testid="wl-email-sending-inherited-link"
    >
      <bdi>{children}</bdi>
    </Button>
  );
}

function SenderStatus({
  own, dirty, fallbackLine, smtpError,
}: {
  own: OwnSender;
  dirty: boolean;
  fallbackLine: string;
  smtpError: (error: SmtpError) => JSX.Element;
}) {
  const { t } = useLanguage();
  const when = (iso: string | null) => formatExactTime(iso ?? undefined);
  if (dirty || own.status !== 'active') {
    return (
      <div className="wl-email-sending-status wl-email-sending-status--pending" data-testid="wl-email-sending-status">
        <strong>{t('whiteLabel.emailSending.statusTestRequired')}</strong>
        <div>{fallbackLine}</div>
        {!dirty && own.last_test_error && (
          <div className="wl-email-sending-status-failure">
            {t('whiteLabel.emailSending.lastTestFailed', { at: when(own.last_test_at) })}
            {smtpError(own.last_test_error)}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="wl-email-sending-status wl-email-sending-status--active" data-testid="wl-email-sending-status">
      <strong>{t('whiteLabel.emailSending.statusActive', { at: when(own.active_since) })}</strong>
      {own.last_failure && (
        <div className="wl-email-sending-status-failure">
          {t('whiteLabel.emailSending.lastSendFailed', { at: when(own.last_failure.at) })}
          {smtpError(own.last_failure)}
          <div>{t('whiteLabel.emailSending.lastSendFellBack')}</div>
        </div>
      )}
    </div>
  );
}
